import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPuzzlePiece, faPlus, faEdit, faTrash, faSearch,
    faSave, faTimes, faSync, faToggleOn, faToggleOff,
    faDatabase, faLink, faSort
} from '@fortawesome/free-solid-svg-icons';
import { Modal, Spinner } from 'react-bootstrap';
import request from '../../../../../request';
import Notify from '../../../../compartidos/Notify';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/';

const EMPTY_MODULE = {
    ModuleKey: '', ModuleLabel: '', ModuleIcon: '',
    ModuleRoute: '', ModuleType: 'STATIC', QueryId: '',
    ModuleOrder: 0, ModuleActive: 1
};

/**
 * ModuleABM — CRUD for tf_module table.
 * Manages the catalog of screens/views that can be assigned to menu groups.
 */
const ModuleABM = () => {
    const [modules,  setModules]  = useState([]);
    const [queries,  setQueries]  = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [search,   setSearch]   = useState('');
    const [modal,    setModal]    = useState(false);
    const [saving,   setSaving]   = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [form,     setForm]     = useState(EMPTY_MODULE);
    const [editId,   setEditId]   = useState(null);

    useEffect(() => { 
        fetchModules();
        fetchQueries();
    }, []);

    const fetchModules = () => {
        setLoading(true);
        request.Get(API_BASE + 'superadmin/modules', {}, r => {
            if (r.success) setModules(r.data || []);
            else Notify({ type: 'fail', text: 'Error al cargar módulos' });
            setLoading(false);
        }, 'json', () => setLoading(false));
    };

    const fetchQueries = () => {
        request.Get(API_BASE + 'superadmin/queries', {}, r => {
            if (r.success) setQueries(r.data || []);
        });
    };

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return modules.filter(m =>
            !q || m.ModuleKey?.toLowerCase().includes(q) || m.ModuleLabel?.toLowerCase().includes(q)
        );
    }, [modules, search]);

    const openNew = () => {
        setForm({ ...EMPTY_MODULE });
        setEditId(null);
        setModal(true);
    };

    const openEdit = (m) => {
        setForm({
            ModuleKey: m.ModuleKey, ModuleLabel: m.ModuleLabel,
            ModuleIcon: m.ModuleIcon || '', ModuleRoute: m.ModuleRoute || '',
            ModuleType: m.ModuleType, QueryId: m.QueryId || '',
            ModuleOrder: m.ModuleOrder, ModuleActive: m.ModuleActive
        });
        setEditId(m.ModuleId);
        setModal(true);
    };

    const closeModal = () => { if (!saving) { setModal(false); setForm(EMPTY_MODULE); setEditId(null); } };

    const handleSave = () => {
        if (!form.ModuleKey || !form.ModuleLabel) {
            Notify({ type: 'fail', text: 'Key y Etiqueta son obligatorios' });
            return;
        }
        setSaving(true);
        const payload = { ...form, QueryId: form.ModuleType === 'DYNAMIC' ? form.QueryId : null };

        const done = (r) => {
            if (r.success) {
                Notify({ type: 'success', text: editId ? 'Módulo actualizado' : 'Módulo creado' });
                fetchModules();
                closeModal();
            } else {
                Notify({ type: 'fail', text: r.msg || 'Error al guardar' });
            }
            setSaving(false);
        };
        const fail = () => setSaving(false);

        if (editId) {
            request.Put(API_BASE + `superadmin/modules/${editId}`, payload, done, 'json', fail);
        } else {
            request.Post(API_BASE + 'superadmin/modules', payload, done, 'json', fail);
        }
    };

    const handleDelete = (m) => {
        setDeleting(m.ModuleId);
        request.Delete(API_BASE + `superadmin/modules/${m.ModuleId}`, {}, r => {
            if (r.success) {
                Notify({ type: 'success', text: 'Módulo eliminado' });
                setModules(prev => prev.filter(x => x.ModuleId !== m.ModuleId));
            } else {
                Notify({ type: 'fail', text: r.msg || 'No se puede eliminar (puede estar en uso)' });
            }
            setDeleting(null);
        }, 'json', () => setDeleting(null));
    };

    const toggleActive = (m) => {
        request.Put(API_BASE + `superadmin/modules/${m.ModuleId}`, { ...m, ModuleActive: m.ModuleActive ? 0 : 1 }, r => {
            if (r.success) {
                setModules(prev => prev.map(x => x.ModuleId === m.ModuleId
                    ? { ...x, ModuleActive: m.ModuleActive ? 0 : 1 }
                    : x
                ));
            }
        });
    };

    const f = (key, val) => setForm(p => ({ ...p, [key]: val }));

    return (
        <div>
            {/* Toolbar */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div className="sa-search-bar" style={{ flex: 1 }}>
                    <FontAwesomeIcon icon={faSearch} style={{ color: '#475569' }} />
                    <input placeholder="Buscar módulo..." value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <div className="d-flex gap-2">
                    <button className="sa-btn sa-btn-ghost" onClick={fetchModules} disabled={loading}>
                        <FontAwesomeIcon icon={faSync} spin={loading} />
                    </button>
                    <button className="sa-btn sa-btn-primary" onClick={openNew}>
                        <FontAwesomeIcon icon={faPlus} /> Nuevo Módulo
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="sa-panel">
                {loading ? (
                    <div className="text-center py-5"><Spinner animation="grow" style={{ color: 'var(--sa-primary)' }} /></div>
                ) : (
                    <div className="table-responsive">
                        <table className="sa-table">
                            <thead>
                                <tr>
                                    <th><FontAwesomeIcon icon={faSort} className="me-1" />Ord</th>
                                    <th>Key</th>
                                    <th>Etiqueta</th>
                                    <th>Tipo</th>
                                    <th>Ruta / QueryId</th>
                                    <th>Activo</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 && (
                                    <tr><td colSpan={7} className="text-center py-4 text-muted">Sin módulos registrados</td></tr>
                                )}
                                {filtered.map(m => (
                                    <tr key={m.ModuleId}>
                                        <td style={{ color: '#475569', width: 50 }}>{m.ModuleOrder}</td>
                                        <td>
                                            <code style={{ color: 'var(--sa-primary)', fontSize: '0.78rem', background: 'rgba(168,85,247,0.08)', padding: '2px 8px', borderRadius: 6 }}>
                                                {m.ModuleKey}
                                            </code>
                                        </td>
                                        <td style={{ fontWeight: 600, fontSize: '0.88rem' }}>{m.ModuleLabel}</td>
                                        <td>
                                            <span className={`sa-badge ${m.ModuleType === 'DYNAMIC' ? 'sa-badge-warning' : 'sa-badge-premium'}`}>
                                                <FontAwesomeIcon icon={m.ModuleType === 'DYNAMIC' ? faDatabase : faLink} />
                                                {m.ModuleType}
                                            </span>
                                        </td>
                                        <td style={{ fontSize: '0.78rem', color: '#64748b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {m.ModuleType === 'DYNAMIC' ? `QueryId: ${m.QueryId || '-'}` : m.ModuleRoute || '-'}
                                        </td>
                                        <td>
                                            <button
                                                className={`sa-btn sa-btn-sm ${m.ModuleActive ? 'sa-btn-success' : 'sa-btn-ghost'}`}
                                                onClick={() => toggleActive(m)}
                                            >
                                                <FontAwesomeIcon icon={m.ModuleActive ? faToggleOn : faToggleOff} />
                                            </button>
                                        </td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <button className="sa-btn sa-btn-ghost sa-btn-sm" onClick={() => openEdit(m)}>
                                                    <FontAwesomeIcon icon={faEdit} />
                                                </button>
                                                <button className="sa-btn sa-btn-danger sa-btn-sm" onClick={() => handleDelete(m)} disabled={deleting === m.ModuleId}>
                                                    {deleting === m.ModuleId ? <Spinner size="sm" animation="border" /> : <FontAwesomeIcon icon={faTrash} />}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal */}
            <Modal show={modal} onHide={closeModal} centered dialogClassName="sa-modal">
                <Modal.Header closeButton className="border-0">
                    <Modal.Title style={{ fontWeight: 800, color: '#f8fafc', fontSize: '1.1rem' }}>
                        <FontAwesomeIcon icon={faPuzzlePiece} style={{ color: 'var(--sa-primary)', marginRight: 8 }} />
                        {editId ? 'Editar Módulo' : 'Nuevo Módulo'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex flex-column gap-3">
                        <div className="row g-3">
                            <div className="col-6">
                                <label className="sa-form-label">Key (único) *</label>
                                <input className="sa-input" placeholder="ej: empresas" value={form.ModuleKey}
                                    onChange={e => f('ModuleKey', e.target.value)} />
                            </div>
                            <div className="col-6">
                                <label className="sa-form-label">Etiqueta *</label>
                                <input className="sa-input" placeholder="ej: Empresas" value={form.ModuleLabel}
                                    onChange={e => f('ModuleLabel', e.target.value)} />
                            </div>
                        </div>
                        <div className="row g-3">
                            <div className="col-6">
                                <label className="sa-form-label">Ícono (FontAwesome)</label>
                                <input className="sa-input" placeholder="ej: faBuilding" value={form.ModuleIcon}
                                    onChange={e => f('ModuleIcon', e.target.value)} />
                            </div>
                            <div className="col-6">
                                <label className="sa-form-label">Tipo</label>
                                <select className="sa-input" value={form.ModuleType} onChange={e => f('ModuleType', e.target.value)}>
                                    <option value="STATIC">STATIC (componente fijo)</option>
                                    <option value="DYNAMIC">DYNAMIC (query dinámica)</option>
                                </select>
                            </div>
                        </div>
                        {form.ModuleType === 'STATIC' && (
                            <div>
                                <label className="sa-form-label">Ruta React Router</label>
                                <input className="sa-input" placeholder="ej: /Turnero/Admin" value={form.ModuleRoute}
                                    onChange={e => f('ModuleRoute', e.target.value)} />
                            </div>
                        )}
                        {form.ModuleType === 'DYNAMIC' && (
                            <div>
                                <label className="sa-form-label">Consulta Asociada (cr_autq) *</label>
                                <select className="sa-input" value={form.QueryId} onChange={e => f('QueryId', e.target.value)}>
                                    <option value="">Seleccionar consulta...</option>
                                    {queries.map(q => (
                                        <option key={q.AutQId} value={q.AutQId}>
                                            {q.AutQTitle} (ID: {q.AutQId})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div className="row g-3">
                            <div className="col-6">
                                <label className="sa-form-label">Orden</label>
                                <input className="sa-input" type="number" value={form.ModuleOrder}
                                    onChange={e => f('ModuleOrder', parseInt(e.target.value) || 0)} />
                            </div>
                            <div className="col-6">
                                <label className="sa-form-label">Estado</label>
                                <select className="sa-input" value={form.ModuleActive} onChange={e => f('ModuleActive', parseInt(e.target.value))}>
                                    <option value={1}>Activo</option>
                                    <option value={0}>Inactivo</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <button className="sa-btn sa-btn-ghost" onClick={closeModal}><FontAwesomeIcon icon={faTimes} /> Cancelar</button>
                    <button className="sa-btn sa-btn-primary" onClick={handleSave} disabled={saving}>
                        {saving ? <Spinner size="sm" animation="border" /> : <><FontAwesomeIcon icon={faSave} /> Guardar</>}
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ModuleABM;
